import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bed, Bath, MapPin, Home, Phone, ChevronLeft, ChevronRight, X, Check, CalendarCheck2, CheckCircle2, Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { loadLeaflet } from "@/lib/leaflet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { t, fmtPrice } from "@/lib/terms";
import { api, mediaUrl } from "@/lib/api";
import { statusBadge, fmtListingPrice } from "@/lib/propertyStatus";
import { buildPropertyJsonLd, buildBreadcrumbJsonLd } from "@/lib/seo";

// Deliberately coarse viewing slots — the buyer says roughly when suits,
// the agent confirms the exact time when they come back to them.
const PREFERRED_DAYS = ["Any day", "Weekday morning", "Weekday afternoon", "Weekday evening", "Saturday", "Sunday"];

// One enquiry per property per visitor per ten minutes. The endpoint is IP
// rate-limited server-side too — this just stops a double-tap (or a bored
// visitor) landing the same lead twice in the agent's inbox.
const ENQUIRY_COOLDOWN_MS = 10 * 60 * 1000;

function LocationMap({ latitude, longitude, label }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    loadLeaflet().then((L) => {
      if (cancelled || !containerRef.current) return;

      const map = L.map(containerRef.current, {
        center: [latitude, longitude],
        zoom: 15,
        scrollWheelZoom: false,
      });
      mapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      const icon = L.divIcon({
        className: "wb-map-target",
        html: `<div style="width:22px;height:22px;border-radius:50%;background:#1A1A1A;border:3px solid #FDFBF7;box-shadow:0 0 0 2px #1A1A1A;"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      L.marker([latitude, longitude], { icon }).addTo(map).bindPopup(label || "");

      setTimeout(() => map.invalidateSize(), 100);
    }).catch(() => {
      // No map rather than a broken page — the listing itself is the content.
    });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [latitude, longitude, label]);

  return <div ref={containerRef} data-testid="listing-map" style={{ height: 300, width: "100%" }} />;
}

/** "2026-09-01" → "1 September 2026". Anything unparseable is left alone. */
function fmtAvailableFrom(iso, m) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(m === "us" ? "en-US" : "en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * Everything the listing page knows that the shared RealEstateListing builder
 * doesn't — key features, energy rating, tenure, room count and the postcode
 * centroid. Emitted for the AI crawlers that read this page rather than visit
 * it, and for rich results. Fields absent from the record are simply omitted.
 */
function withListingExtras(ld, prop, coords) {
  if (!ld) return ld;
  const about = { ...(ld.about || {}) };
  if (prop.bedrooms) about.numberOfBedrooms = prop.bedrooms;
  if (prop.reception_rooms) about.numberOfRooms = (prop.bedrooms || 0) + prop.reception_rooms;
  if (coords) {
    about.geo = { "@type": "GeoCoordinates", latitude: coords.latitude, longitude: coords.longitude };
  }
  if (prop.key_features?.length) {
    about.amenityFeature = prop.key_features.map((f) => ({
      "@type": "LocationFeatureSpecification", name: f, value: true,
    }));
  }
  const extras = [
    prop.tenure && { name: "Tenure", value: prop.tenure },
    prop.council_tax_band && { name: "Council tax band", value: prop.council_tax_band },
  ].filter(Boolean).map((p) => ({ "@type": "PropertyValue", ...p }));
  if (extras.length) about.additionalProperty = extras;
  return { ...ld, about };
}

/** Public property detail page for the shop-window marketplace. */
export default function PublicListingDetail() {
  const { slug: urlSlug, id } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const isUs = market === "us";
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(urlSlug);
  const [prop, setProp] = useState(null);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(-1);
  const [coords, setCoords] = useState(null);
  const [enquiry, setEnquiry] = useState({
    name: "", email: "", phone: "", preferred: PREFERRED_DAYS[0], message: "", consent: false, company: "",
  });
  const [sending, setSending] = useState(false);
  const [enquirySent, setEnquirySent] = useState(false);
  const setEnq = (k) => (v) => setEnquiry((f) => ({ ...f, [k]: v?.target?.value ?? v }));

  // On a tenant's own domain there is no slug in the URL, so `tenantSlug` is
  // the "foundation" fallback above until the brand lookup comes back with
  // the real one. Asking under a tenant that doesn't own the property gets a
  // 404, and the render checks `error` first — so a live, on-the-market house
  // told buyers "this property is no longer available".
  //
  // Cancelling the superseded request was not enough on its own. It only
  // helps when the brand lands first; measured on production the 404 came
  // back BEFORE the brand did, so `error` was set while nothing had
  // superseded it yet, and every listing on a custom domain flashed
  // "no longer available" before correcting itself. On a slow connection
  // that flash is long enough to read and leave.
  //
  // So don't guess the tenant at all: fetch once we actually know it. That is
  // immediately when the URL carries the slug (/t/:slug/...), and otherwise
  // when the brand lookup has settled. `brand_resolved` is set in a `finally`,
  // so it becomes true even if that lookup fails — a page that never loads is
  // not a failure mode this can reach. Net effect on a custom domain is one
  // request instead of two, and the one that used to 404 is the one removed.
  useEffect(() => {
    if (!tenantKnown) return undefined;
    let superseded = false;
    api.get(`/crm/public/properties/${id}?tenant=${tenantSlug}`)
      .then(({ data }) => {
        if (superseded) return;
        setProp(data);
        setError(false);
      })
      .catch(() => { if (!superseded) setError(true); });
    return () => { superseded = true; };
  }, [id, tenantSlug, tenantKnown]);

  // Postcode centroid for the location map. postcodes.io is the same free
  // service the valuation engine geocodes with server-side, and it's UK-only —
  // a US listing (or any failure) simply renders no map.
  useEffect(() => {
    if (isUs || !prop?.postcode) return;
    let cancelled = false;
    fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(prop.postcode.trim())}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const res = d?.result;
        if (!cancelled && res?.latitude != null) {
          setCoords({ latitude: res.latitude, longitude: res.longitude });
        }
      })
      .catch(() => { /* no map, no fuss */ });
    return () => { cancelled = true; };
  }, [isUs, prop?.postcode]);

  const price = prop && fmtListingPrice(prop, market);

  // Order images so the primary shows first
  const images = React.useMemo(() => {
    if (!prop?.images?.length) return [];
    const primary = prop.images.find((i) => i.id === prop.primary_image_id);
    const rest = prop.images.filter((i) => i.id !== prop.primary_image_id);
    return primary ? [primary, ...rest] : prop.images;
  }, [prop]);

  // The floor plan rides along at the end of the media set so it opens in the
  // same lightbox as the photos rather than needing a viewer of its own.
  const media = React.useMemo(() => (
    prop?.floorplan?.url
      ? [...images, { id: "floorplan", url: prop.floorplan.url, alt: "Floor plan" }]
      : images
  ), [images, prop]);

  // Material information the CRM already holds. Each row appears only when the
  // record actually carries it, so a sparse listing degrades to nothing.
  const details = prop ? [
    { label: "Property type", value: prop.property_type },
    !isUs && { label: "Tenure", value: prop.tenure },
    !isUs && { label: "Council tax band", value: prop.council_tax_band },
    prop.listing_type === "letting" && { label: "Deposit", value: prop.deposit_amount ? fmtPrice(prop.deposit_amount, market) : "" },
    prop.listing_type === "letting" && { label: "Available from", value: fmtAvailableFrom(prop.available_from, market) },
  ].filter((d) => d && d.value) : [];

  // Keyboard navigation for the lightbox
  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight") setLightbox((i) => Math.min(media.length - 1, i + 1));
      if (e.key === "ArrowLeft") setLightbox((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, media.length]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!enquiry.name.trim() || !enquiry.email.trim()) {
      toast.error("Please add your name and email so we can come back to you.");
      return;
    }
    if (!enquiry.consent) {
      toast.error("Please tick the consent box so we're allowed to contact you.");
      return;
    }
    // Honeypot and throttle both behave exactly as a real send would — a bot
    // learns nothing from the response, and nothing reaches the lead inbox.
    let throttled = false;
    try {
      throttled = Date.now() - Number(window.sessionStorage.getItem(`fdn_enquiry_${id}`) || 0) < ENQUIRY_COOLDOWN_MS;
    } catch (err) { /* private browsing — fall through and send */ }
    if (enquiry.company || throttled) { setEnquirySent(true); return; }

    setSending(true);
    try {
      const parts = enquiry.name.trim().split(/\s+/);
      const where = [prop.address_line1, prop.town, prop.postcode].filter(Boolean).join(", ");
      await api.post("/crm/public/register-applicant", {
        tenant_slug: tenantSlug,
        property_id: prop.id,
        first_name: parts[0],
        last_name: parts.slice(1).join(" "),
        email: enquiry.email.trim().toLowerCase(),
        phone: enquiry.phone.trim(),
        listing_type: prop.listing_type === "letting" ? "letting" : "sale",
        notes: [
          `${isUs ? "Showing" : "Viewing"} request from the website: ${where}`,
          `Preferred day: ${enquiry.preferred}`,
          enquiry.message.trim(),
        ].filter(Boolean).join("\n"),
        source: "listing_enquiry",
        marketing_optin: false,
        gdpr_consent: true,
      });
      try {
        window.sessionStorage.setItem(`fdn_enquiry_${id}`, String(Date.now()));
      } catch (err) { /* ignore */ }
      setEnquirySent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your request. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  const seoTitle = prop
    ? `${prop.address_line1}, ${prop.town || ""}, ${price} | ${biz.name || "Property"}`
    : undefined;
  const seoImage = prop?.images?.[0]?.url ? mediaUrl(prop.images[0].url) : undefined;
  // Derive breadcrumb URLs from the current URL rather than reconstructing
  // a tenant prefix — works identically on /t/:slug/properties/:id (shared
  // domain) and /properties/:id (a tenant's own verified custom domain).
  const propertiesUrl = window.location.href.replace(/\/properties\/.*$/, "/properties");
  const homeUrl = window.location.href.replace(/\/properties\/.*$/, "") || `${window.location.origin}/`;
  const breadcrumbs = prop ? buildBreadcrumbJsonLd([
    { name: "Home", url: homeUrl },
    { name: "Properties", url: propertiesUrl },
    { name: prop.address_line1, url: window.location.href.split("?")[0] },
  ]) : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={seoTitle}
        description={prop?.description?.slice(0, 200)}
        image={seoImage}
        jsonLd={prop ? [withListingExtras(buildPropertyJsonLd(prop, brand, { url: window.location.href, resolveImageUrl: mediaUrl }), prop, coords), breadcrumbs] : null}
        noindex={!prop}
      />
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12">
          <Link to={tenantPath("properties")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" />Back to all properties
          </Link>

          {error ? (
            <div className="mt-16 text-center text-muted-foreground" data-testid="public-detail-not-found">
              This property is no longer available. <Link to={tenantPath("properties")} className="text-primary underline">Browse current listings</Link>.
            </div>
          ) : !prop ? (
            <div className="mt-16 text-center text-muted-foreground">Loading…</div>
          ) : (
            <>
              {/* Photo hero */}
              {images.length > 0 ? (
                <div className="mt-8 grid grid-cols-4 gap-2 rounded-sm overflow-hidden">
                  <button
                    onClick={() => setLightbox(0)}
                    data-testid="hero-main-photo"
                    className="col-span-4 sm:col-span-3 aspect-[4/3] overflow-hidden group"
                  >
                    <img
                      src={mediaUrl(images[0].url)}
                      alt={prop.address_line1}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </button>
                  <div className="hidden sm:grid grid-rows-3 gap-2">
                    {images.slice(1, 4).map((img, idx) => (
                      <button
                        key={img.id}
                        onClick={() => setLightbox(idx + 1)}
                        data-testid={`hero-thumb-${idx}`}
                        className="aspect-[4/3] overflow-hidden relative"
                      >
                        <img
                          src={mediaUrl(img.thumb_url || img.url)}
                          alt=""
                          className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                          loading="lazy"
                        />
                        {idx === 2 && images.length > 4 && (
                          <div className="absolute inset-0 bg-primary/60 flex items-center justify-center text-background text-sm font-medium">
                            +{images.length - 4} more
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-8 aspect-[16/9] bg-secondary/40 rounded-sm flex items-center justify-center border border-border">
                  <Home className="w-12 h-12 text-muted-foreground/40" />
                </div>
              )}

              <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground flex items-center gap-3">
                      {prop.listing_type === "letting" ? t("to_let", market) : "For sale"} · {prop.property_type}
                      {statusBadge(prop.status, prop.listing_type, market) && (
                        <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground tracking-normal normal-case text-[11px]">
                          {statusBadge(prop.status, prop.listing_type, market).label}
                        </span>
                      )}
                    </div>
                    <h1 className="font-serif-display text-4xl text-primary mt-3">{prop.address_line1}</h1>
                    <div className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {prop.town} · {prop.postcode}
                    </div>
                  </div>

                  <div className="border-y border-border py-6 flex items-center gap-8 flex-wrap">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">{prop.listing_type === "letting" ? "Rent" : (isUs ? "List price" : "Asking price")}</div>
                      <div className="font-serif-display text-4xl text-primary">{price}</div>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground sm:border-l sm:border-border sm:pl-8">
                      <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {prop.bedrooms} bed</span>
                      <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {prop.bathrooms} bath</span>
                      {prop.size_sqft > 0 && <span>{prop.size_sqft.toLocaleString()} sqft</span>}
                    </div>
                  </div>

                  {prop.description && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">About this home</div>
                      <p className="mt-3 text-primary leading-relaxed whitespace-pre-wrap">
                        {prop.description}
                      </p>
                    </div>
                  )}

                  {prop.key_features?.length > 0 && (
                    <div data-testid="listing-key-features">
                      <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Key features</div>
                      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                        {prop.key_features.map((f, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-primary leading-relaxed">
                            <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />{f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prop.floorplan?.url && (
                    <div data-testid="listing-floorplan">
                      <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Floor plan</div>
                      <button
                        onClick={() => setLightbox(media.length - 1)}
                        className="mt-3 w-full border border-border bg-card rounded-sm p-4 flex items-center justify-center hover:border-accent transition-colors duration-300"
                        data-testid="listing-floorplan-open"
                        aria-label="Open the floor plan full size"
                      >
                        <img
                          src={mediaUrl(prop.floorplan.url)}
                          alt="Floor plan"
                          className="max-h-[420px] w-auto object-contain"
                          loading="lazy"
                        />
                      </button>
                    </div>
                  )}

                  {details.length > 0 && (
                    <div className="border-t border-border pt-6" data-testid="listing-details">
                      <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Property details</div>
                      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4">
                        {details.map((d) => (
                          <div key={d.label}>
                            <dt className="text-xs uppercase tracking-widest text-muted-foreground">{d.label}</dt>
                            <dd className="text-primary mt-1">{d.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}

                  {coords && (
                    <div data-testid="listing-location">
                      <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Location</div>
                      <div className="mt-3 border border-border rounded-sm overflow-hidden">
                        <LocationMap latitude={coords.latitude} longitude={coords.longitude} label={prop.address_line1} />
                        <div className="px-4 py-3 text-xs text-muted-foreground bg-card border-t border-border">
                          Approximate location, centred on {prop.postcode}.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* The WHOLE column sticks, not the enquiry card inside it.
                    A sticky element is only offset visually — siblings still
                    lay out against the position it left behind — so with just
                    the card sticky, "Marketed by" slid up underneath it and
                    the two rendered on top of each other.

                    Bounded height because the column carries a form: taller
                    than the viewport and it pins by its top, putting its own
                    submit button permanently out of reach on a short screen. */}
                <div className="space-y-4 sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
                  <div className="border border-accent/40 bg-accent/5 rounded-sm p-5">
                    <div className="text-xs uppercase tracking-[0.28em] text-accent">Interested?</div>
                    {enquirySent ? (
                      <div data-testid="enquiry-sent">
                        <CheckCircle2 className="w-8 h-8 text-accent mt-4" />
                        <div className="font-serif-display text-2xl text-primary mt-3">Request sent</div>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          We'll be in touch about {prop.address_line1}, usually the same day.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                          {isUs ? "Schedule a showing" : "Arrange a viewing"} or ask us anything about this property.
                        </p>
                        <form onSubmit={submitEnquiry} className="mt-4 space-y-3" data-testid="enquiry-form">
                          <div>
                            <Label htmlFor="enquiry-name">Your name</Label>
                            <Input id="enquiry-name" value={enquiry.name} onChange={setEnq("name")} data-testid="enquiry-name" required />
                          </div>
                          <div>
                            <Label htmlFor="enquiry-email">Email</Label>
                            <Input id="enquiry-email" type="email" value={enquiry.email} onChange={setEnq("email")} data-testid="enquiry-email" required />
                          </div>
                          <div>
                            <Label htmlFor="enquiry-phone">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Input id="enquiry-phone" value={enquiry.phone} onChange={setEnq("phone")} data-testid="enquiry-phone" />
                          </div>
                          <div>
                            <Label>Preferred day</Label>
                            <Select value={enquiry.preferred} onValueChange={setEnq("preferred")}>
                              <SelectTrigger data-testid="enquiry-preferred"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {PREFERRED_DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="enquiry-message">Anything else? <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Textarea
                              id="enquiry-message"
                              rows={3}
                              maxLength={600}
                              value={enquiry.message}
                              onChange={setEnq("message")}
                              placeholder="Times that suit you, questions about the property…"
                              data-testid="enquiry-message"
                            />
                          </div>
                          {/* Honeypot — off-screen for people, irresistible to bots. */}
                          <input
                            type="text"
                            name="company"
                            value={enquiry.company}
                            onChange={setEnq("company")}
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            className="absolute w-px h-px opacity-0 left-[-9999px]"
                          />
                          <label className="flex items-start gap-3 cursor-pointer text-xs">
                            <input
                              type="checkbox"
                              data-testid="enquiry-consent"
                              className="mt-0.5"
                              checked={enquiry.consent}
                              onChange={(e) => setEnquiry((f) => ({ ...f, consent: e.target.checked }))}
                            />
                            <span className="text-muted-foreground leading-relaxed">
                              I'm happy for {biz.name || "the agency"} to contact me about this property and to store my details in line with their{brand.legal?.privacy ? (
                                <> <Link to={tenantPath("privacy")} className="underline underline-offset-2">privacy policy</Link>.</>
                              ) : " privacy policy."}
                            </span>
                          </label>
                          <Button type="submit" className="w-full rounded-sm" size="lg" disabled={sending} data-testid="enquiry-submit">
                            {sending
                              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                              : <><CalendarCheck2 className="w-4 h-4 mr-2" />{isUs ? "Request a showing" : "Request a viewing"}</>}
                          </Button>
                        </form>
                      </>
                    )}
                    {biz.phone && (
                      <a href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>
                        <Button variant="outline" className="w-full rounded-sm mt-3">
                          <Phone className="w-4 h-4 mr-2" />Call {biz.phone}
                        </Button>
                      </a>
                    )}
                    <Link
                      to={tenantPath("register")}
                      className="block text-center text-xs uppercase tracking-[0.24em] text-muted-foreground hover:text-primary transition-colors mt-4"
                    >
                      Register for alerts
                    </Link>
                  </div>
                  <div className="border border-border rounded-sm p-5 text-sm text-muted-foreground">
                    <div className="text-xs uppercase tracking-widest text-primary">Marketed by</div>
                    <div className="mt-2 text-primary font-medium">{biz.name}</div>
                    {biz.email && <div className="mt-1">{biz.email}</div>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />

      {/* Lightbox */}
      {lightbox >= 0 && media[lightbox] && (
        <div
          className="fixed inset-0 z-50 bg-primary/95 flex items-center justify-center"
          onClick={() => setLightbox(-1)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-6 right-6 text-background/80 hover:text-background p-2"
            onClick={(e) => { e.stopPropagation(); setLightbox(-1); }}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          {lightbox > 0 && (
            <button
              className="absolute left-6 text-background/80 hover:text-background p-2"
              onClick={(e) => { e.stopPropagation(); setLightbox((i) => i - 1); }}
              aria-label="Previous"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          <img
            src={mediaUrl(media[lightbox].url)}
            alt={media[lightbox].alt || ""}
            className="max-h-[92vh] max-w-[92vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {lightbox < media.length - 1 && (
            <button
              className="absolute right-6 text-background/80 hover:text-background p-2"
              onClick={(e) => { e.stopPropagation(); setLightbox((i) => i + 1); }}
              aria-label="Next"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-widest text-background/70">
            {lightbox + 1} / {media.length}
          </div>
        </div>
      )}
    </div>
  );
}
